import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import { SITE_NAME, SITE_OG_IMAGE, SITE_URL, siteContent } from "@/content/site";
import { absoluteLocalizedUrl, stripLocalePrefix } from "@/i18n/localePath";
import { localizeJsonLd } from "@/seo/jsonLd";

type JsonLd = Record<string, unknown>;

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  nofollow?: boolean;
  jsonLd?: JsonLd | JsonLd[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const DEFAULT_IMAGE = SITE_OG_IMAGE;
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  image = DEFAULT_IMAGE,
  imageAlt,
  type = "website",
  noindex = false,
  nofollow = false,
  jsonLd,
  article,
}: SEOProps) => {
  const { locale } = useTranslation();
  const { pathname } = useLocation();

  // Canonicals and language alternates always resolve to separate, paired URLs.
  const canonicalPath = canonical
    ? new URL(canonical, SITE_URL).pathname
    : pathname;
  const cleanBasePath = stripLocalePrefix(canonicalPath).replace(/\/+$/, "") || "/";
  const englishUrl = absoluteLocalizedUrl(SITE_URL, cleanBasePath, "en");
  const frenchUrl = absoluteLocalizedUrl(SITE_URL, cleanBasePath, "fr");
  const url = locale === "fr" ? frenchUrl : englishUrl;
  const resolvedImageAlt = imageAlt ?? (locale === "fr"
    ? siteContent.seo.defaultImageAltFr
    : siteContent.seo.defaultImageAlt);

  // Sync <html lang> with active locale.
  useEffect(() => {
    document.documentElement.lang = locale === "fr" ? "fr-CA" : "en-CA";
  }, [locale]);

  const ogLocale = locale === "fr" ? "fr_CA" : "en_CA";
  const ogLocaleAlt = locale === "fr" ? "en_CA" : "fr_CA";

  const robots = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
    "max-image-preview:large",
    "max-snippet:-1",
    "max-video-preview:-1",
  ].join(",");

  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];
  const localizedJsonLdArray = jsonLdArray.map((block) => localizeJsonLd(block, locale));

  return (
    <Helmet>
      {/* Primary meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />

      {/* Canonical + reciprocal hreflang on distinct language URLs. */}
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en-CA" href={englishUrl} />
      <link rel="alternate" hrefLang="fr-CA" href={frenchUrl} />
      <link rel="alternate" hrefLang="x-default" href={englishUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />

      {/* Article-specific OG */}
      {type === "article" && article?.publishedTime ? (
        <meta property="article:published_time" content={article.publishedTime} />
      ) : null}
      {type === "article" && article?.modifiedTime ? (
        <meta property="article:modified_time" content={article.modifiedTime} />
      ) : null}
      {type === "article" && article?.author ? (
        <meta property="article:author" content={article.author} />
      ) : null}
      {type === "article" && article?.section ? (
        <meta property="article:section" content={article.section} />
      ) : null}
      {type === "article" && article?.tags
        ? article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))
        : null}

      {/* JSON-LD */}
      {localizedJsonLdArray.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
